// src/components/AppBar.js
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TodayIcon from '@mui/icons-material/Today';
import Logout from '@mui/icons-material/Logout';
import Person from '@mui/icons-material/Person';
import Email from '@mui/icons-material/Email';
import Business from '@mui/icons-material/Business';
import Badge from '@mui/icons-material/Badge';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../hooks/useToast';
import { getUserRole } from '../utils/helpers';
import { companyInfo } from '../utils/dataInfo';

const drawerWidth = 250;

const AppBar = styled(MuiAppBar, {
shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
zIndex: theme.zIndex.drawer + 1,
transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
}),
...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
    }),
}),
}));

export default function CustomAppBar({ open, toggleDrawer }) {
const [anchorEl, setAnchorEl] = useState(null);
const [userInfo, setUserInfo] = useState(null);
const [userRole, setUserRole] = useState('');
const [currentDate, setCurrentDate] = useState('');
const navigate = useNavigate();
const showToast = useCustomToast();

useEffect(() => {
    // Check if user is authenticated
    const checkAuth = () => {
    const userData = sessionStorage.getItem('userInfo');
    
    if (!userData) {
        // User is not logged in, redirect to login
        showToast("Warning", "Please login to continue", "warning");
        navigate('/');
        return;
    }
    
    try {
        const parsedUserInfo = JSON.parse(userData);
        setUserInfo(parsedUserInfo);
        
        if (parsedUserInfo.role) {
        const roleName = getUserRole(parsedUserInfo.role);
        setUserRole(roleName);
        }
    } catch (error) {
        console.error('Error parsing user info:', error);
        // Clear invalid session data
        sessionStorage.removeItem('userInfo');
        sessionStorage.removeItem('token');
        navigate('/');
    }
    };

    checkAuth();

    // Set current date
    const today = new Date();
    setCurrentDate(today.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
    }));
}, [navigate, showToast]);

const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
};

const handleClose = () => {
    setAnchorEl(null);
};

const handleLogout = () => {
    // Clear user session
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('token');
    navigate('/');
    showToast("Warning", "You Have Logged Out From The System", "warning");
    handleClose();
};

const getStatusDisplay = (status) => {
    return status === "1" || status === 1 ? "Active" : "Inactive";
};

const getStatusIcon = (status) => {
    return status === "1" || status === 1 ? 
    <CheckCircle sx={{ color: 'success.main', fontSize: '1rem' }} /> : 
    <Cancel sx={{ color: 'error.main', fontSize: '1rem' }} />;
};

// Don't render the AppBar if user is not logged in
if (!userInfo) {
    return null; // Or you could return a skeleton/loading state
}

return (
    <AppBar position="absolute" open={open}>
    <Toolbar>
        <IconButton
        edge="start"
        color="inherit"
        aria-label="open drawer"
        onClick={toggleDrawer}
        sx={{
            marginRight: '36px',
            ...(open && { display: 'none' }),
        }}
        >
        <MenuIcon />
        </IconButton>

        <Typography
        component="h1"
        variant="h6"
        color="inherit"
        noWrap
        sx={{ flexGrow: 1 }}
        >
        {companyInfo.name} Management System
        </Typography>

        {/* Today's Date */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 2 }}>
        <TodayIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
        <Typography variant="body2">
            {currentDate}
        </Typography>
        </Box>

        {/* User Info */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Chip
            icon={<AccountCircle color='white' />}
            label={userInfo.name}
            variant="outlined"
            onClick={handleMenu}
            sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
            }}
        />

        <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
            sx: {
                mt: 1.5,
                minWidth: 280,
                maxWidth: 350,
            }
            }}
        >
            {/* User Info Section */}
            <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                User Profile
            </Typography>
            
            {/* Name with Role */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Person sx={{ fontSize: '1.1rem', mr: 1.5, color: 'primary.main' }} />
                <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {userInfo.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {userRole || getUserRole(userInfo.role)}
                </Typography>
                </Box>
            </Box>

            {/* Email */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Email sx={{ fontSize: '1.1rem', mr: 1.5, color: 'primary.main' }} />
                <Box>
                <Typography variant="body2">
                    {userInfo.email}
                </Typography>
                </Box>
            </Box>

            {/* Branch */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Business sx={{ fontSize: '1.1rem', mr: 1.5, color: 'primary.main' }} />
                <Box>
                <Typography variant="body2">
                    Branch: {userInfo.branch_name || 'No Branch Assigned'} 
                </Typography>
                </Box>
            </Box>

            {/* Status */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Badge sx={{ fontSize: '1.1rem', mr: 1.5, color: 'primary.main' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getStatusIcon(userInfo.status)}
                <Typography variant="body2">
                    Status: {getStatusDisplay(userInfo.status)}
                </Typography>
                </Box>
            </Box>
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            {/* Logout Option */}
            <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
            <Logout sx={{ mr: 1.5, fontSize: '1.25rem' }} />
            Logout
            </MenuItem>
        </Menu>
        </Box>
    </Toolbar>
    </AppBar>
);
}